import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bot, 
  Send, 
  Sparkles, 
  User,
  Loader2,
  Lightbulb,
  Target,
  Folder
} from 'lucide-react';
import { DbChallenge, DbChallengeSolution, useChallengeSession, useUpdateChallenge } from '@/hooks/useChallengeLab';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MarkdownErrorBoundary } from './MarkdownErrorBoundary';

// Envuelve cada <table> del markdown en un contenedor con scroll horizontal
// para que las tablas anchas no queden recortadas dentro del ScrollArea del chat.
const mdComponents = {
  table: ({ node, ...props }: any) => (
    <div className="overflow-x-auto max-w-full my-2">
      <table className="w-max min-w-full border-collapse text-xs" {...props} />
    </div>
  ),
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChallengeAssistantPanelProps {
  activeChallenge: DbChallenge | null;
  onCreateDraftChallenge: () => void;
  onUpdateChallenge: (updates: Partial<DbChallenge>) => void;
  onViewChallengeInfo: () => void;
  onViewSolutions: () => void;
  solutions: DbChallengeSolution[];
}

const suggestedQuestions = [
  "Tengo un proceso con muchos errores manuales",
  "Quiero optimizar un flujo de trabajo lento",
  "Necesito automatizar tareas repetitivas",
  "Quiero crear un dashboard con mis datos",
];

const initialMessage: Message = {
  role: 'assistant',
  content: '¡Hola! 👋 Soy tu consultor de **LIDER IA**. Mi objetivo es que hoy no solo encuentres un problema, sino que diseñemos una solución de IA que transforme tu área de trabajo.\n\nPara empezar, cuéntame: **¿En qué área trabajas y cuál es ese proceso crítico que hoy te genera fricción, lentitud o errores?** Describe paso a paso cómo lo haces actualmente y qué herramientas usas.'
};

export function ChallengeAssistantPanel({
  activeChallenge,
  onCreateDraftChallenge,
  onUpdateChallenge,
  onViewChallengeInfo,
  onViewSolutions,
  solutions,
}: ChallengeAssistantPanelProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([initialMessage]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasCreatedChallenge, setHasCreatedChallenge] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const updateChallenge = useUpdateChallenge();
  const initialLoadDoneRef = useRef<string | null>(null);
  // Permite cancelar streams en curso al cambiar de reto o al desmontar el panel:
  // libera al worker del backend y deja de drenar tokens del gateway.
  const abortControllerRef = useRef<AbortController | null>(null);

  const { session, chatMessages, isLoading: sessionLoading, createSession, addMessage } = useChallengeSession(activeChallenge?.id || null);

  // Load persisted messages ONLY when challenge changes (not on every chatMessages update)
  useEffect(() => {
    if (!activeChallenge) {
      setHasCreatedChallenge(false);
      setMessages([initialMessage]);
      setCurrentSessionId(null);
      initialLoadDoneRef.current = null;
      return;
    }

    setHasCreatedChallenge(true);

    // Only load from DB once per challenge, and only after session data is ready
    if (sessionLoading) return;
    if (initialLoadDoneRef.current === activeChallenge.id) return;

    if (session && chatMessages.length > 0) {
      const loaded: Message[] = chatMessages.map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));
      setMessages([initialMessage, ...loaded]);
      setCurrentSessionId(session.id);
    } else {
      setMessages([initialMessage]);
      setCurrentSessionId(null);
    }

    initialLoadDoneRef.current = activeChallenge.id;
  }, [activeChallenge?.id, sessionLoading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cancelar stream activo cuando se cambia de reto o se desmonta el panel.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, [activeChallenge?.id]);

  const streamChallengeAssistant = async (allMessages: Message[], signal: AbortSignal) => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) {
      throw new Error('No active session. Please sign in again.');
    }

    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/challenge-assistant`,
      {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({
          messages: allMessages.map(m => ({ role: m.role, content: m.content })),
          challengeId: activeChallenge?.id || null,
          userId: user?.id || null,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response;
  };

  const processToolCall = (toolCallData: { function?: { name?: string; arguments?: string } }) => {
    if (toolCallData.function?.name === 'extract_challenge_metadata') {
      try {
        const args = JSON.parse(toolCallData.function.arguments || '{}');
        const updates: Partial<DbChallenge> = {};
        if (args.title) updates.title = args.title;
        if (args.description) updates.description = args.description;
        if (args.target_audience) updates.target_audience = args.target_audience;
        if (args.kpi_name) updates.kpi_name = args.kpi_name;
        if (args.kpi_base_value) updates.kpi_base_value = args.kpi_base_value;
        if (args.kpi_expected_value) updates.kpi_expected_value = args.kpi_expected_value;
        updates.status = 'active';
        onUpdateChallenge(updates);
      } catch (e) {
        console.error('Tool call parse error:', e);
      }
    }

    if (toolCallData.function?.name === 'register_challenge_solution') {
      // Solution was already inserted server-side; invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['challenge_solutions'] });
      queryClient.invalidateQueries({ queryKey: ['all_challenge_solutions'] });
    }
  };

  const persistMessages = useCallback(async (userText: string, assistantText: string) => {
    try {
      let sessId = currentSessionId;

      if (!sessId) {
        // Create session with title from first user message
        const title = userText.slice(0, 100);
        sessId = await createSession(title);
        setCurrentSessionId(sessId);

        // Auto-title the challenge if it's still "Nuevo reto"
        if (activeChallenge && activeChallenge.title === 'Nuevo reto') {
          const challengeTitle = userText.slice(0, 60);
          updateChallenge.mutate({ id: activeChallenge.id, title: challengeTitle });
        }
      }

      await addMessage(sessId, 'user', userText);
      await addMessage(sessId, 'assistant', assistantText);
    } catch (err) {
      console.error('Error persisting messages:', err);
    }
  }, [currentSessionId, createSession, addMessage, activeChallenge, updateChallenge]);

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    if (!activeChallenge) {
      console.warn('No active challenge — message not sent');
      return;
    }

    // Cancelar cualquier stream previo (defensa por doble-click o reintento manual)
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const userMessage: Message = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    let serverErrorCode: string | null = null;
    let serverErrorRetryAfter: number | null = null;

    try {
      const response = await streamChallengeAssistant(newMessages, controller.signal);
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let buffer = '';
      let hasToolCall = false;

      setMessages([...newMessages, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const payload = trimmed.slice(6);
          if (payload === '[DONE]') break;

          try {
            const parsed = JSON.parse(payload);

            if (parsed.type === 'content' && parsed.content) {
              assistantContent += parsed.content;
              setMessages([...newMessages, { role: 'assistant', content: assistantContent }]);
            }

            if (parsed.type === 'tool_call') {
              hasToolCall = true;
              const tc = parsed.tool_call;
              processToolCall(tc);
            }

            if (parsed.type === 'error') {
              serverErrorCode = parsed.code || 'stream_error';
              serverErrorRetryAfter = typeof parsed.retry_after === 'number' ? parsed.retry_after : null;
            }
          } catch {
            // skip
          }
        }
      }

      // Si el servidor reportó error tipado, no persistimos ni mostramos respuesta válida.
      if (serverErrorCode) {
        const toastByCode: Record<string, { title: string; description: string }> = {
          rate_limit: {
            title: 'Demasiadas solicitudes',
            description: serverErrorRetryAfter
              ? `El asistente está saturado. Reintenta en ${serverErrorRetryAfter}s.`
              : 'El asistente está saturado. Espera unos segundos y reintenta.',
          },
          credits_exhausted: {
            title: 'Sin créditos del asistente',
            description: 'Contacta al administrador para restaurar el servicio.',
          },
          timeout: {
            title: 'La respuesta tardó demasiado',
            description: 'Reintenta el mensaje. Si persiste, divide tu pregunta en partes más cortas.',
          },
          upstream_error: {
            title: 'Error del modelo',
            description: 'Hubo un problema temporal con el asistente. Reintenta en un momento.',
          },
          stream_error: {
            title: 'Error de conexión',
            description: 'No pudimos completar la respuesta. Reintenta.',
          },
          aborted: {
            title: 'Respuesta cancelada',
            description: 'La conexión se cerró antes de terminar.',
          },
        };
        const t = toastByCode[serverErrorCode] || toastByCode.stream_error;
        toast({ title: t.title, description: t.description, variant: 'destructive' });
        // Removemos el placeholder vacío del assistant.
        setMessages(newMessages);
        return;
      }

      if (!assistantContent) {
        assistantContent = hasToolCall
          ? 'He guardado la información. Continuemos…'
          : 'No recibí respuesta del asistente. Por favor intenta de nuevo.';
      }

      setMessages([...newMessages, { role: 'assistant', content: assistantContent }]);

      await persistMessages(text, assistantContent);
    } catch (error: any) {
      // El abort del cliente (navegar fuera, cambiar de reto, doble-click) NO es un error
      // visible para el usuario: la UI ya se reseteó por el componente que cambió.
      if (error?.name === 'AbortError' || controller.signal.aborted) {
        return;
      }
      console.error('Chat error:', error);
      toast({
        title: 'Error de conexión',
        description: 'No pudimos contactar al asistente. Reintenta.',
        variant: 'destructive',
      });
      setMessages(newMessages);
    } finally {
      // Solo limpiar la ref si no fue reemplazada por un envío posterior.
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Card className="glass-card h-full flex flex-col">
      <CardHeader className="pb-3 border-b border-border/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="flex items-center gap-2">
                Asistente de Reto y Soluciones
                <Sparkles className="w-4 h-4 text-secondary animate-pulse" />
              </span>
              <p className="text-xs font-normal text-muted-foreground">
                Identifica oportunidades de alto impacto
              </p>
            </div>
          </CardTitle>
          
          {activeChallenge && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onViewChallengeInfo} className="gap-1.5">
                <Target className="w-4 h-4" />
                <span className="hidden sm:inline">Ver Reto</span>
              </Button>
              <Button variant="outline" size="sm" onClick={onViewSolutions} className="gap-1.5">
                <Folder className="w-4 h-4" />
                <span className="hidden sm:inline">Soluciones</span>
                {solutions.length > 0 && (
                  <span className="ml-0.5 text-xs bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center">
                    {solutions.length}
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* El div display:table interno de Radix se ensanchaba con tablas/textos largos y
            cortaba los mensajes por la derecha: se fuerza a block para que ajusten al ancho. */}
        <ScrollArea className="flex-1 p-4 [&>[data-radix-scroll-area-viewport]>div]:!block" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  message.role === 'user' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-gradient-to-br from-secondary to-primary text-white'
                }`}>
                  {message.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`min-w-0 max-w-[85%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-tr-sm'
                    : 'bg-muted rounded-tl-sm'
                }`}>
                  {message.role === 'assistant' ? (
                    <div className="text-sm prose prose-sm dark:prose-invert max-w-none break-words [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_code]:break-words [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1">
                      <MarkdownErrorBoundary fallback={<p className="text-sm whitespace-pre-wrap">{message.content}</p>}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{message.content}</ReactMarkdown>
                      </MarkdownErrorBoundary>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                  )}
                </div>
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        {messages.length <= 2 && !activeChallenge && (
          <div className="px-4 pb-3">
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" />
              Sugerencias:
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className="text-xs h-auto py-1.5 px-3 hover:bg-primary/10 hover:border-primary/30"
                  onClick={() => handleSend(question)}
                >
                  {question}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="p-4 border-t border-border/50 bg-background/50">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe tu situación o problema..."
              className="min-h-[44px] max-h-[100px] resize-none bg-muted/50 text-sm"
              rows={1}
            />
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
              className="gradient-primary shrink-0 h-11 w-11"
              size="icon"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
